import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import useAutoFocus from '../../hooks/useAutoFocus';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
} from '@mui/material';

/**
 * A generic "name a project" prompt — used for both creating a new project
 * (GH #275: the tree's "+" button had no way to create a project at all, the
 * only prior project-creation code, `SelectContent`, was never mounted) and
 * copying one from the tree's project right-click menu (#287). `onSubmit`
 * does the actual API call; the dialog itself is just the name prompt +
 * busy/error state, shared so both flows can't drift.
 */
const NewProjectDialog = ({
  open,
  title = 'New project',
  label = 'Project name',
  submitLabel = 'Create',
  onClose,
  onSubmit,
  onDone,
}) => {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // #793/#149: focus the field when the dialog OPENS, without `autoFocus`.
  // Keyed on `open` rather than mount, because this dialog is kept mounted and
  // toggled — `autoFocus` only ever fires on a fresh mount and would silently do
  // nothing on the second open. See hooks/useAutoFocus.
  const focusRef = useAutoFocus(open);

  useEffect(() => {
    if (open) {
      setName('');
      setError('');
      setBusy(false);
    }
  }, [open]);

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError('');
    try {
      const result = await onSubmit(trimmed);
      onDone(result);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed.');
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          inputRef={focusRef}
          fullWidth
          margin="dense"
          label={label}
          value={name}
          disabled={busy}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
          slotProps={{ htmlInput: { 'data-testid': 'new-project-name' } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim() || busy}
          onClick={submit}
          data-testid="new-project-submit"
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

NewProjectDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  title: PropTypes.string,
  label: PropTypes.string,
  submitLabel: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

export default NewProjectDialog;
